import { useState } from 'react'
import type {
	ExperienceMerchandisingInputSchema,
	ExperienceShellMerchOutputSchema,
	ExperienceVettingMerchOutputSchema
} from '@/features/experience-vetting/form-schemas'
import { httpGenerateMerchVettingCopy } from '@/features/ops-vetting/http/ops-vetting.http'
import {
	generateMerchCopyAiPrompt,
	parseMarkdownJson
} from '@/features/ops-vetting/utils'
import type {
	ServiceMerchandisingInputSchema,
	ServiceMerchandisingOutputSchema
} from '@/features/service-vetting/form-schemas'
import { useAsync } from '@/hooks/use-async'

export type AiModel = 'palmyra-x5'
export type MerchInput =
	| ServiceMerchandisingInputSchema
	| ExperienceMerchandisingInputSchema
	| ExperienceShellMerchOutputSchema
export type MerchOutput =
	| ServiceMerchandisingOutputSchema
	| ExperienceShellMerchOutputSchema
	| ExperienceVettingMerchOutputSchema

export const useMerchCopyAi = <T extends MerchOutput>() => {
	const [aiResponse, setAiResponse] = useState<T | undefined>(undefined)
	const { run, isLoading, isError } = useAsync()

	const onMerchCopyAiCall = async (data: MerchInput, customPrompt = '') => {
		const formattedPrompt = generateMerchCopyAiPrompt(data, customPrompt)
		try {
			const { data: responseData } = await run(
				httpGenerateMerchVettingCopy({
					prompt: formattedPrompt,
					model: 'palmyra-x5'
				})
			)
			const parsedResponse = parseMarkdownJson(responseData) as T
			setAiResponse(parsedResponse)
			return responseData
		} catch (e) {
			console.error('Error generating merchandising copy:', e)
			throw e
		}
	}

	const resetAiResponse = () => {
		setAiResponse(undefined)
	}

	return {
		aiResponse,
		isLoading,
		aiError: isError,
		onMerchCopyAiCall,
		resetAiResponse
	}
}
